import {
  ReactNode,
  useMemo,
  useState,
  createContext,
  useContext,
  useRef,
  useEffect,
  useCallback,
} from 'react';

type FilterContextType = {
  openFilterIndex: string | undefined;
  setOpenFilterIndex: (index: string | undefined) => void;
  registerFilter: (key: string) => number;
};

const FilterContext = createContext<FilterContextType | undefined>(undefined);

export const useFilterContext = () => {
  const context = useContext(FilterContext);
  if (!context) {
    return {
      openFilterIndex: undefined,
      setOpenFilterIndex: () => {},
      registerFilter: () => 0,
    };
  }

  return context;
};

type FilterContextProviderProps = {
  children: ReactNode;
};

export const FilterContextProvider = ({ children }: FilterContextProviderProps) => {
  const [openFilterIndex, setOpenFilterIndex] = useState<string | undefined>(undefined);
  const filterIndexMapRef = useRef<Map<string, number>>(new Map());
  const filterIndexCounterRef = useRef<number>(0);

  const registerFilter = useCallback((key: string): number => {
    if (!filterIndexMapRef.current.has(key)) {
      filterIndexMapRef.current.set(key, filterIndexCounterRef.current);
      filterIndexCounterRef.current += 1;
    }

    return filterIndexMapRef.current.get(key)!;
  }, []);

  const filterContextValue = useMemo(
    () => ({
      openFilterIndex,
      setOpenFilterIndex,
      registerFilter,
    }),
    [openFilterIndex, registerFilter],
  );

  // Reset counter and map when component mounts
  useEffect(() => {
    filterIndexMapRef.current.clear();
    filterIndexCounterRef.current = 0;
  }, []);

  return (
    <FilterContext.Provider value={filterContextValue}>{children}</FilterContext.Provider>
  );
};
